import { Modal } from "melodies-source/Modal";
import { Select } from "melodies-source/Select";
import { TextInput } from "melodies-source/TextInput";
import { SvgMail } from "melodies-source/Svgs/Mail";

import { useArtistContext } from "Components";
import { ArtistGroupRole } from "@max/common/artists";
import { Affiliation, AffiliationMap } from "@max/common/dist/signup";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Checkbox } from "melodies-source/Selectable";
import { Confirmation } from "melodies-source/Confirmation";
import { useCustomAppContext } from "contexts/CustomAppContext";
import { cloudCallable } from "@max/common/dist/functions";
import { useFormik } from "formik";
import * as yup from "yup";
import { yupRequired } from "Utils/yup";

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;

  ${({ theme }) => theme.media.desktop} {
    flex-wrap: nowrap;
  }
`;

interface AddUserData {
  artistGroupId: string;
  email: string;
  role: Affiliation;
  isAdmin: boolean;
}

export const addUser = async ({
  artistGroupId,
  email,
  role,
  isAdmin,
}: AddUserData) => {
  const roles: ArtistGroupRole[] = [role === "artist" ? "artist" : "manager"];

  if (isAdmin) {
    roles.push("admin");
  }

  return cloudCallable("signup-account")({
    action: "add-user",
    artistGroupId,
    email,
    roles,
    affiliation: role,
  });
};

interface Values {
  email: string;
  role: Affiliation | undefined;
  isAdmin: boolean;
}

const ADD_USER_VALIDATION_SCHEMA = yup.object().shape({
  email: yupRequired.email("Please enter a valid email"),
});

export const AddTeammateModal = ({ target: Target, ...props }) => {
  const { artistGroupDoc, getArtistGroup, logAction } = useArtistContext();
  const [loading, setLoading] = useState(false);
  const formik = useFormik<Values>({
    initialValues: {
      email: "",
      role: undefined,
      isAdmin: false,
    },
    validationSchema: ADD_USER_VALIDATION_SCHEMA,
    onSubmit: handleSave,
  });
  const [isOpen, setIsOpen] = useState(false);
  const { customApp } = useCustomAppContext();

  async function handleSave(values: Values) {
    setLoading(true);
    const { email, role, isAdmin } = values;
    await addUser({
      artistGroupId: artistGroupDoc.id,
      email,
      role,
      isAdmin,
    });

    await logAction("portal_add_teammate", {
      member: { role, isAdmin, email },
    });

    getArtistGroup();
    setLoading(false);
    toggleModal();
  }

  function toggleModal() {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  return (
    <div>
      <Target {...props} onClick={toggleModal} />
      <Modal
        variant="medium"
        isOpen={isOpen}
        onClose={toggleModal}
        header="Add Another Teammate"
      >
        <Confirmation
          confirmLabel="Add"
          loading={loading}
          onCancel={toggleModal}
          onConfirm={formik.handleSubmit}
        >
          <InputContainer>
            <TextInput
              label="Email address"
              placeholder="Add a teammate by email address..."
              leftIcon={<SvgMail />}
              value={formik.values.email}
              onChange={(email) => formik.setFieldValue("email", email.trim())}
              style={{ flexGrow: 1, minWidth: "60%" }}
              hasError={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
            />
            <Select
              label="Role"
              options={Object.entries(customApp?.userRoles || AffiliationMap)
                .filter(([_, label]) => label.toLowerCase() !== "unknown")
                .map(([value, label]) => ({
                  value,
                  label,
                }))}
              value={formik.values.role}
              onChange={(role: Affiliation) =>
                formik.setFieldValue("role", role)
              }
            />
          </InputContainer>
          <Checkbox
            style={{ marginTop: 20 }}
            description="Admin users are able to add and remove other users on the account and may have access to billing information and fan PII."
            label="Make Admin"
            value={formik.values.isAdmin}
            onChange={(isAdmin) => formik.setFieldValue("isAdmin", isAdmin)}
          />
        </Confirmation>
      </Modal>
    </div>
  );
};
